import { create } from 'zustand';
import { DETECTIVE_TOUR_LENGTH } from '@/tour/detectiveTour';

/**
 * "Run the Detective" guided-tour state.
 *
 * Lives in a store rather than component state because the tour navigates
 * between routes: every step unmounts the previous page, so the position in
 * the narrative has to survive outside the route tree.
 */
interface DetectiveTourState {
  active: boolean;
  stepIndex: number;

  start: () => void;
  next: () => void;
  back: () => void;
  exit: () => void;
  goTo: (index: number) => void;
}

/** Clamp into the valid step range; an empty tour clamps to 0. */
function clampIndex(index: number): number {
  const max = Math.max(DETECTIVE_TOUR_LENGTH - 1, 0);
  if (!Number.isFinite(index)) return 0;
  return Math.min(Math.max(Math.trunc(index), 0), max);
}

export const useDetectiveTourStore = create<DetectiveTourState>((set) => ({
  active: false,
  stepIndex: 0,

  start: () => set({ active: true, stepIndex: 0 }),

  // Next past the final step ends the tour — the last step's primary button is
  // "Finish", so falling off the end is the intended exit, not a dead click.
  next: () =>
    set((s) => {
      if (!s.active) return s;
      if (s.stepIndex >= DETECTIVE_TOUR_LENGTH - 1) {
        return { active: false, stepIndex: 0 };
      }
      return { stepIndex: s.stepIndex + 1 };
    }),

  // Back on step 1 is a no-op rather than an exit: a presenter tapping ← at
  // the start should not lose the tour in front of a room.
  back: () =>
    set((s) => {
      if (!s.active) return s;
      return { stepIndex: clampIndex(s.stepIndex - 1) };
    }),

  exit: () => set({ active: false, stepIndex: 0 }),

  goTo: (index) =>
    set((s) => (s.active ? { stepIndex: clampIndex(index) } : s)),
}));
